import { useEffect } from 'react'

const SITE_URL = 'https://factory-nexus.ai'
const DEFAULT_TITLE = 'Factory Nexus'
// Keep in sync with the static <meta name="description"> in index.html
const DEFAULT_DESCRIPTION =
  'AI coding agent platform: describe a feature and up to 5 parallel AI agents build it on isolated git branches, pass AI code review, and ship PR-ready code to GitHub.'

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

interface PageMeta {
  title: string
  description: string
  /** Route path used for the canonical URL, e.g. '/products' */
  path: string
  /** Optional robots directive, e.g. 'noindex, follow' on the 404 page. */
  robots?: string
}

export function usePageMeta({ title, description, path, robots }: PageMeta) {
  useEffect(() => {
    document.title = title
    upsertMeta('name', 'description', description)
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:url', SITE_URL + path)
    if (robots) upsertMeta('name', 'robots', robots)

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = SITE_URL + path

    return () => {
      document.title = DEFAULT_TITLE
      upsertMeta('name', 'description', DEFAULT_DESCRIPTION)
      upsertMeta('property', 'og:title', DEFAULT_TITLE)
      upsertMeta('property', 'og:description', DEFAULT_DESCRIPTION)
      upsertMeta('property', 'og:url', SITE_URL + '/')
      document.head.querySelector('link[rel="canonical"]')?.remove()
      document.head.querySelector('meta[name="robots"]')?.remove()
    }
  }, [title, description, path, robots])
}
